import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, ArrowRight, CheckCircle2, ShieldCheck, Calculator, IdCard } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGridSkeleton } from "@/components/FyndanuGrid";
import { VehicleBrowser } from "@/components/VehicleBrowser";
import { fyndanuListingsQuery } from "@/lib/vehicles";
import { HUSVAGN_BRANDS } from "@/data/vehicle-brands";
import { buildListingHead, type ListingLike } from "@/lib/listing-seo";


const URL_SELF = "https://www.akhusbilfritid.se/husvagnar-till-salu";

const FAQ = [
  {
    q: "Vilka husvagnar har ni till salu?",
    a: "Vi har alltid ett aktuellt lager av kontrollerade husvagnar — Kabe, Polar, Adria, Cabby, Knaus m.fl. Filtrera på märke, pris, årsmodell och antal bäddar direkt på sidan.",
  },
  {
    q: "Kan ni leverera husvagnen hem till mig?",
    a: "Ja, vi ordnar transport i hela Sverige mot kostnad. Vi hjälper också till med registreringsbesiktning och överlämning.",
  },
  {
    q: "Hur vet jag att husvagnen är felfri?",
    a: "Alla våra husvagnar fukttestas, gasoltestas och genomgås teknisk innan försäljning. Vi berättar öppet både det som är bra och det som behöver åtgärdas.",
  },
  {
    q: "Tar ni in min gamla husvagn i inbyte?",
    a: "Ja — inbyten är en stor del av vår verksamhet. Ring 070-304 53 44 eller kom förbi Stabsvägen 5 i Kalix så värderar vi.",
  },
];

export const Route = createFileRoute("/husvagnar-till-salu")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  // Titel, beskrivning och schema byggs från verkligt lager (antal, prisspann,
  // märken) — samma URL och canonical som tidigare, bara vassare metadata.
  head: ({ loaderData }) =>
    buildListingHead({
      category: "husvagn",
      items: (loaderData as { items?: ListingLike[] } | undefined)?.items ?? [],
      url: URL_SELF,
      fiUrl: "https://www.akhusbilfritid.se/fi/asuntovaunut",
      faq: FAQ,
      fallbackBrands: HUSVAGN_BRANDS.map((b) => b.name),
    }),

  component: HusvagnarPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-20" role="alert">Något gick fel: {error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => <div className="container-edge py-20">Sidan hittades inte.</div>,
});

function HusvagnarPage() {
  return (
    <>
      <PageHeader
        label="Husvagnar · Fukttestade & prissatta rakt"
        title={<>Husvagnar till salu <span className="font-italic-display text-accent">i Kalix</span>.</>}
        sub="Kabe, Polar, Adria och fler — filtrera på märke, pris, årsmodell och bäddar. Vi ordnar transport i hela Sverige."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/husvagn-kalix" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Husvagnar i Kalix — visning & inbyte <ArrowRight size={14} />
          </Link>
          <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Söker du husbil? <ArrowRight size={14} />
          </Link>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Värdera inbyte <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-14">
        <Suspense fallback={<FyndanuGridSkeleton />}>
          <VehicleBrowser category="husvagn" emptyLabel="Inga husvagnar matchar filtret just nu. Prova att rensa filtret eller ring så letar vi." />
        </Suspense>
      </section>

      <section className="container-edge py-10">
        <p className="eyebrow">Bläddra per märke</p>
        <h2 className="mt-3 font-display text-2xl md:text-4xl">Populära husvagnsmärken</h2>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {HUSVAGN_BRANDS.map((b) => (
            <Link key={b.slug} to="/husvagnar/$brand" params={{ brand: b.slug }} className="group rounded-2xl border border-border bg-card p-4 text-center transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg">
              <p className="font-display text-lg group-hover:text-accent">{b.name}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{b.origin.split(" ")[0]}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-edge py-6">
        <div className="grid gap-4 md:grid-cols-2">
          <Link to="/verktyg/manadskostnad-husbil" className="group flex items-center gap-4 rounded-3xl border border-border bg-card p-6 transition hover:border-accent hover:shadow-lg">
            <div className="rounded-full bg-primary/10 p-3 text-accent"><Calculator size={22} /></div>
            <div>
              <p className="font-display text-lg group-hover:text-accent">Räkna månadskostnad</p>
              <p className="text-sm text-muted-foreground">Pris, kontantinsats & ränta → månadsbelopp direkt.</p>
            </div>
            <ArrowRight size={18} className="ml-auto text-accent" />
          </Link>
          <Link to="/verktyg/korkort-husbil" className="group flex items-center gap-4 rounded-3xl border border-border bg-card p-6 transition hover:border-accent hover:shadow-lg">
            <div className="rounded-full bg-primary/10 p-3 text-accent"><IdCard size={22} /></div>
            <div>
              <p className="font-display text-lg group-hover:text-accent">Vilket körkort krävs?</p>
              <p className="text-sm text-muted-foreground">Ange totalvikt → se om B, B96, BE eller C1 krävs.</p>
            </div>
            <ArrowRight size={18} className="ml-auto text-accent" />
          </Link>
        </div>
      </section>


      <section className="bg-secondary py-16">
        <div className="container-edge">
          <p className="eyebrow">Så köper du tryggt hos oss</p>
          <div className="mt-6 grid gap-6 md:grid-cols-4">
            {[
              { i: <ShieldCheck size={20} />, t: "Fukttestad", d: "Varje husvagn fukttestas innan leverans." },
              { i: <CheckCircle2 size={20} />, t: "Raka priser", d: "Ingen dold påslagning — priset är priset." },
              { i: <CheckCircle2 size={20} />, t: "Inbyte välkommet", d: "Vi tar in husvagnar och husbilar kontinuerligt." },
              { i: <CheckCircle2 size={20} />, t: "Transport i hela Sverige", d: "Vi ordnar leverans dörr till dörr." },
            ].map((x) => (
              <div key={x.t} className="rounded-3xl border border-border bg-card p-6">
                <div className="inline-flex rounded-full bg-primary/10 p-2 text-accent">{x.i}</div>
                <h3 className="mt-4 font-display text-xl">{x.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-16">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl">Att köpa husvagn hos AK</h2>
        <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card">
          {FAQ.map((f, i) => (
            <details key={i} className="group p-6">
              <summary className="cursor-pointer list-none font-display text-lg">{f.q}</summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
